import { useEffect, useState } from "react";
import { openCalendly } from "../../utils/calendly";
import { apiService } from "../../services/api";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

interface Plan {
  id: string;
  name: string;
  features: string[];
}

export default function Pricing() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await apiService.getPlans();
        if (response.data) {
          setPlans(response.data);
        }
      } catch (err) {
        console.error("Failed to fetch plans:", err);
        setError("Unable to load plans right now. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const handlePricingClick = () => {
    openCalendly();
  };

  return (
    <Container
      maxWidth="md"
      id="pricing"
      sx={{
        pt: { xs: 4, sm: 8 },
        pb: { xs: 6, sm: 12 },
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: { xs: 3, sm: 6 },
      }}
    >
      <Box
        sx={{
          width: { sm: "100%", md: "60%" },
          textAlign: { sm: "left", md: "center" },
        }}
      >
        <Typography
          component="h2"
          variant="h4"
          gutterBottom
          sx={{ color: "text.primary" }}
        >
          Simple, Transparent Plans
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          Choose the plan that fits your team. Book a demo and we'll walk you
          through the right option.
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error" variant="body1">
          {error}
        </Typography>
      ) : (
        <Grid
          container
          spacing={3}
          sx={{ alignItems: "center", justifyContent: "center", width: "100%" }}
        >
          {plans.map((plan, index) => {
            const isFeatured = index === 1;
            const features = Array.isArray(plan.features) ? plan.features : [];

            return (
              <Grid key={plan.id} size={{ xs: 12, sm: 6, md: 4 }}>
                <Card
                  sx={[
                    {
                      p: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                      height: "100%",
                    },
                    isFeatured &&
                      ((theme) => ({
                        border: "none",
                        background:
                          "radial-gradient(circle at 50% 0%, hsl(220, 20%, 35%), hsl(220, 30%, 6%))",
                        boxShadow: `0 8px 12px hsla(220, 20%, 42%, 0.2)`,
                        ...theme.applyStyles("dark", {
                          background:
                            "radial-gradient(circle at 50% 0%, hsl(220, 20%, 20%), hsl(220, 30%, 16%))",
                          boxShadow: `0 8px 12px hsla(0, 0%, 0%, 0.8)`,
                        }),
                      })),
                  ]}
                >
                  <CardContent>
                    <Box
                      sx={[
                        {
                          mb: 1,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 2,
                        },
                        isFeatured ? { color: "grey.100" } : { color: "" },
                      ]}
                    >
                      <Typography component="h3" variant="h6">
                        {plan.name}
                      </Typography>
                      {isFeatured && (
                        <Chip
                          icon={<AutoAwesomeIcon />}
                          label="Most Popular"
                        />
                      )}
                    </Box>
                    <Divider
                      sx={{ my: 2, opacity: 0.8, borderColor: "divider" }}
                    />
                    {features.map((feature, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          py: 1,
                          display: "flex",
                          gap: 1.5,
                          alignItems: "center",
                        }}
                      >
                        <CheckCircleRoundedIcon
                          sx={[
                            { width: 20 },
                            isFeatured
                              ? { color: "primary.light" }
                              : { color: "secondary.main" },
                          ]}
                        />
                        <Typography
                          variant="subtitle2"
                          component="span"
                          sx={[
                            isFeatured ? { color: "grey.50" } : { color: null },
                          ]}
                        >
                          {feature}
                        </Typography>
                      </Box>
                    ))}
                  </CardContent>
                  <CardActions>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handlePricingClick}
                      sx={{
                        backgroundImage: "none",
                        backgroundColor: "hsl(210, 98%, 42%)",
                        color: "white",
                        border: "none",
                        boxShadow: "none",
                        "&:hover": {
                          backgroundImage: "none",
                          backgroundColor: "hsl(210, 100%, 35%)",
                          boxShadow: "none",
                        },
                      }}
                    >
                      Book a Demo
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Container>
  );
}
